import { create } from 'zustand';
import type { SpellAreaEffect, SpellAreaPayload, EffectName, EffectIntensity, SpellShape } from '@machina/core';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Effect Store — manages spell area effects on the map.
 *
 * Persisted to `machina_spell_effects` (survives reload).
 * Broadcast via Supabase realtime for instant cross-player sync.
 */

// ─── DB row shape ────────────────────────────────────────────
interface DbSpellEffect {
  id: string;
  campaign_id: string;
  name: string;
  origin_x: number;
  origin_y: number;
  radius_squares: number;
  shape: string;
  effect: string;
  intensity: string;
  alpha: number;
  owner_id: string | null;
  token_id: string | null;
  created_at: string;
}

function dbToEffect(row: DbSpellEffect): SpellAreaEffect {
  return {
    id: row.id,
    name: row.name,
    origin: { x: row.origin_x, y: row.origin_y },
    radiusSquares: row.radius_squares,
    shape: row.shape as SpellShape,
    effect: row.effect as EffectName,
    intensity: row.intensity as EffectIntensity,
    alpha: row.alpha,
    ownerId: row.owner_id ?? undefined,
    tokenId: row.token_id ?? undefined,
    createdAt: new Date(row.created_at).getTime(),
  };
}

function effectToDb(effect: SpellAreaEffect, campaignId: string): Omit<DbSpellEffect, 'created_at'> {
  return {
    id: effect.id,
    campaign_id: campaignId,
    name: effect.name,
    origin_x: effect.origin.x,
    origin_y: effect.origin.y,
    radius_squares: effect.radiusSquares,
    shape: effect.shape,
    effect: effect.effect,
    intensity: effect.intensity,
    alpha: effect.alpha,
    owner_id: effect.ownerId ?? null,
    token_id: effect.tokenId ?? null,
  };
}

// ─── Store ───────────────────────────────────────────────────

interface EffectState {
  activeEffects: Map<string, SpellAreaEffect>;
  selectedEffectId: string | null;
  campaignId: string | null;

  addEffect: (effect: SpellAreaEffect) => void;
  removeEffect: (id: string) => void;
  moveEffect: (id: string, origin: { x: number; y: number }) => void;
  selectEffect: (id: string | null) => void;
  deleteSelected: () => void;
  clearAll: () => void;

  /** Load effects from DB + subscribe to realtime broadcast */
  subscribe: (campaignId: string) => void;
  unsubscribe: () => void;

  channel: RealtimeChannel | null;
}

export const useEffectStore = create<EffectState>((set, get) => ({
  activeEffects: new Map(),
  selectedEffectId: null,
  campaignId: null,
  channel: null,

  addEffect: (effect) => {
    set((state) => {
      const newEffects = new Map(state.activeEffects);
      newEffects.set(effect.id, effect);
      return { activeEffects: newEffects };
    });

    // Persist to DB
    const { campaignId } = get();
    if (campaignId) {
      supabase
        .from('machina_spell_effects')
        .upsert(effectToDb(effect, campaignId))
        .then(({ error }) => {
          if (error) console.warn('[effectStore] DB insert failed:', error.message);
        });
    }

    // Broadcast to other players
    const { channel } = get();
    if (channel) {
      channel.send({
        type: 'broadcast',
        event: 'spell-effect',
        payload: { type: 'add', effect } satisfies SpellAreaPayload,
      });
    }
  },

  removeEffect: (id) => {
    set((state) => {
      const newEffects = new Map(state.activeEffects);
      newEffects.delete(id);
      return { activeEffects: newEffects, selectedEffectId: state.selectedEffectId === id ? null : state.selectedEffectId };
    });

    // Remove from DB
    supabase
      .from('machina_spell_effects')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) console.warn('[effectStore] DB delete failed:', error.message);
      });

    const { channel } = get();
    if (channel) {
      channel.send({
        type: 'broadcast',
        event: 'spell-effect',
        payload: { type: 'remove', effectId: id } satisfies SpellAreaPayload,
      });
    }
  },

  moveEffect: (id, origin) => {
    set((state) => {
      const existing = state.activeEffects.get(id);
      if (!existing) return state;
      const newEffects = new Map(state.activeEffects);
      newEffects.set(id, { ...existing, origin });
      return { activeEffects: newEffects };
    });

    // Update DB
    supabase
      .from('machina_spell_effects')
      .update({ origin_x: origin.x, origin_y: origin.y })
      .eq('id', id)
      .then(({ error }) => {
        if (error) console.warn('[effectStore] DB move failed:', error.message);
      });

    const { channel } = get();
    if (channel) {
      channel.send({
        type: 'broadcast',
        event: 'spell-effect',
        payload: { type: 'move', effectId: id, origin } satisfies SpellAreaPayload,
      });
    }
  },

  selectEffect: (id) => {
    set({ selectedEffectId: id });
  },

  deleteSelected: () => {
    const { selectedEffectId } = get();
    if (selectedEffectId) {
      get().removeEffect(selectedEffectId);
    }
  },

  clearAll: () => {
    set({ activeEffects: new Map() });

    // Clear from DB
    const { campaignId } = get();
    if (campaignId) {
      supabase
        .from('machina_spell_effects')
        .delete()
        .eq('campaign_id', campaignId)
        .then(({ error }) => {
          if (error) console.warn('[effectStore] DB clear failed:', error.message);
        });
    }

    const { channel } = get();
    if (channel) {
      channel.send({
        type: 'broadcast',
        event: 'spell-effect',
        payload: { type: 'clear' } satisfies SpellAreaPayload,
      });
    }
  },

  subscribe: (campaignId) => {
    get().unsubscribe();
    set({ campaignId });

    // Load existing effects from DB
    supabase
      .from('machina_spell_effects')
      .select('*')
      .eq('campaign_id', campaignId)
      .then(({ data, error }) => {
        if (error) {
          console.warn('[effectStore] Failed to load effects:', error.message);
          return;
        }
        if (data && data.length > 0) {
          const effects = new Map<string, SpellAreaEffect>();
          for (const row of data as DbSpellEffect[]) {
            effects.set(row.id, dbToEffect(row));
          }
          set({ activeEffects: effects });
        }
      });

    // Subscribe to broadcast for real-time sync
    const channel = supabase
      .channel(`effects-${campaignId}`)
      .on('broadcast', { event: 'spell-effect' }, (msg) => {
        const payload = msg.payload as SpellAreaPayload;

        if (payload.type === 'add' && payload.effect) {
          set((state) => {
            const newEffects = new Map(state.activeEffects);
            newEffects.set(payload.effect!.id, payload.effect!);
            return { activeEffects: newEffects };
          });
        } else if (payload.type === 'remove' && payload.effectId) {
          set((state) => {
            const newEffects = new Map(state.activeEffects);
            newEffects.delete(payload.effectId!);
            return { activeEffects: newEffects };
          });
        } else if (payload.type === 'move' && payload.effectId && payload.origin) {
          set((state) => {
            const existing = state.activeEffects.get(payload.effectId!);
            if (!existing) return state;
            const newEffects = new Map(state.activeEffects);
            newEffects.set(payload.effectId!, { ...existing, origin: payload.origin! });
            return { activeEffects: newEffects };
          });
        } else if (payload.type === 'clear') {
          set({ activeEffects: new Map() });
        }
      })
      .subscribe();

    set({ channel });
  },

  unsubscribe: () => {
    const { channel } = get();
    if (channel) {
      supabase.removeChannel(channel);
      set({ channel: null });
    }
  },
}));
